import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, Clock, Search, X } from 'lucide-react';
import type { Track } from '@pujolisique/shared';
import { useSuggest } from '../api/hooks';
import { addRecentSearch, clearRecentSearches, removeRecentSearch, useRecentSearches } from '../lib/recent';
import { usePlayer } from '../player/store';
import { Cover } from './Cover';

interface Item {
  key: string;
  icon: ReactNode;
  title: string;
  sub?: string;
  kind?: string;
  run: () => void;
  onRemove?: () => void;
}

/**
 * Champ de recherche de la barre du haut :
 *  - vide : recherches récentes ;
 *  - pendant la frappe : suggestions (albums, artistes, histoires, titres avec leur pochette) ;
 *  - Entrée : page de résultats complète. Sur cette page, les résultats suivent la frappe.
 */
export function SearchBox() {
  const nav = useNavigate();
  const loc = useLocation();
  const [params] = useSearchParams();
  const onSearchPage = loc.pathname === '/search';
  const [q, setQ] = useState(onSearchPage ? (params.get('q') ?? '') : '');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [term, setTerm] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const debounce = useRef<number>(undefined);
  const recent = useRecentSearches();
  const playTracks = usePlayer((s) => s.playTracks);
  const suggest = useSuggest(onSearchPage ? '' : term);

  // Hors de la page de recherche, le champ se vide ; sur la page, il suit l'adresse (bouton Précédent)
  useEffect(() => {
    if (!onSearchPage) setQ('');
    else setQ(params.get('q') ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.pathname, onSearchPage && params.get('q')]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target as HTMLElement).closest('input,textarea,select')) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, []);

  const go = (value: string) => {
    const v = value.trim();
    if (!v) return;
    addRecentSearch(v);
    setOpen(false);
    setQ(v);
    nav(`/search?q=${encodeURIComponent(v)}`, { replace: onSearchPage });
  };

  const onChange = (v: string) => {
    setQ(v);
    setActive(-1);
    setOpen(true);
    window.clearTimeout(debounce.current);
    debounce.current = window.setTimeout(() => {
      if (onSearchPage) nav(`/search${v.trim() ? `?q=${encodeURIComponent(v.trim())}` : ''}`, { replace: true });
      else setTerm(v.trim());
    }, onSearchPage ? 220 : 160);
  };

  const done = (fn: () => void) => () => {
    if (q.trim()) addRecentSearch(q);
    setOpen(false);
    input.current?.blur();
    fn();
  };

  const items: Item[] = useMemo(() => {
    if (!q.trim()) {
      return recent.map((r) => ({
        key: `r-${r}`,
        icon: <Clock size={16} />,
        title: r,
        run: () => go(r),
        onRemove: () => removeRecentSearch(r),
      }));
    }
    if (onSearchPage) return [];
    const d = suggest.data;
    const list: Item[] = [{ key: 'all', icon: <Search size={16} />, title: `Tous les résultats pour « ${q.trim()} »`, run: () => go(q) }];
    if (!d || term !== q.trim()) return list;
    if (d.corrected) list[0].sub = `Vouliez-vous dire « ${d.corrected} » ?`;
    for (const a of d.albums.slice(0, 3))
      list.push({ key: `a-${a.id}`, icon: <Cover art={`album/${a.id}`} name={a.name} size={64} />, title: a.name, sub: a.artists.map((x) => x.name).join(', '), kind: 'Album', run: done(() => nav(`/album/${a.id}`)) });
    for (const p of d.artists.slice(0, 2))
      list.push({ key: `p-${p.id}`, icon: <Cover art={`artist/${p.id}`} name={p.name} size={64} round />, title: p.name, kind: 'Artiste', run: done(() => nav(`/artist/${p.id}`)) });
    for (const c of d.composers.slice(0, 2))
      list.push({ key: `c-${c.id}`, icon: <Cover art={`composer/${c.id}`} name={c.name} size={64} round />, title: c.name, kind: 'Compositeur', run: done(() => nav(`/composer/${c.id}`)) });
    for (const s of d.series.slice(0, 2))
      list.push({ key: `s-${s.id}`, icon: <Cover art={s.cover} name={s.name} size={64} />, title: s.name, sub: s.parent, kind: 'Histoire', run: done(() => nav(`/stories/${s.id}`)) });
    const play = (t: Track) => done(() => playTracks([t], 0, { source: `Recherche « ${q.trim()} »` }));
    for (const t of d.tracks.slice(0, 3))
      list.push({ key: `t-${t.id}`, icon: <Cover art={t.cover} name={t.album ?? t.title} size={64} />, title: t.title, sub: t.artists.map((x) => x.name).join(', '), kind: 'Titre', run: play(t) });
    for (const h of (d.lyrics ?? []).slice(0, 2))
      list.push({
        key: `l-${h.track.id}`,
        icon: <Cover art={h.track.cover} name={h.track.title} size={64} />,
        title: h.track.title,
        sub: h.excerpt.replace(/[\u0002\u0003]/g, ''),
        kind: 'Paroles',
        run: play(h.track),
      });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, term, recent, suggest.data, onSearchPage]);

  const show = open && items.length > 0;

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' && items.length) {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === 'ArrowUp' && items.length) {
      e.preventDefault();
      setActive((i) => (i <= 0 ? items.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (show && active >= 0) items[active].run();
      else go(q);
    } else if (e.key === 'Escape') {
      if (show) setOpen(false);
      else if (q) onChange('');
    }
  };

  return (
    <div className="searchbox" ref={box}>
      <label className="search">
        <Search size={18} aria-hidden />
        <input
          ref={input}
          type="search"
          role="combobox"
          aria-expanded={show}
          aria-controls="search-suggest"
          aria-activedescendant={show && active >= 0 ? `sugg-${active}` : undefined}
          aria-autocomplete="list"
          placeholder={window.matchMedia('(max-width: 820px)').matches ? 'Rechercher' : 'Titres, albums, artistes, paroles…'}
          value={q}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label="Rechercher dans la bibliothèque"
          enterKeyHint="search"
        />
        {q ? (
          <button
            type="button"
            className="search__clear"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange('');
              input.current?.focus();
            }}
            aria-label="Effacer la recherche"
            title="Effacer"
          >
            <X size={16} />
          </button>
        ) : (
          <kbd className="search__kbd" aria-hidden>
            /
          </kbd>
        )}
      </label>
      {show && (
        <div className="suggest" id="search-suggest" role="listbox" aria-label={q.trim() ? 'Suggestions' : 'Recherches récentes'}>
          {!q.trim() && (
            <div className="suggest__head">
              <span>Recherches récentes</span>
              <button className="link-quiet" onMouseDown={(e) => e.preventDefault()} onClick={() => clearRecentSearches()}>
                Effacer
              </button>
            </div>
          )}
          {items.map((it, i) => (
            <div
              key={it.key}
              id={`sugg-${i}`}
              role="option"
              aria-selected={i === active}
              className={`suggest__item ${i === active ? 'is-active' : ''} ${it.key === 'all' ? 'suggest__item--all' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={it.run}
            >
              <span className="suggest__icon">{it.icon}</span>
              <span className="suggest__text">
                <span className="suggest__title">{it.title}</span>
                {it.sub && <span className="suggest__sub">{it.sub}</span>}
              </span>
              {it.kind && <span className="suggest__kind">{it.kind}</span>}
              {it.key === 'all' && <ArrowUpRight size={16} className="muted" aria-hidden />}
              {it.onRemove && (
                <button
                  className="icon-btn suggest__remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    it.onRemove!();
                  }}
                  aria-label={`Retirer « ${it.title} » de l’historique`}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
